import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import ActionMenu from 'common/ActivityChip/ActionMenu';
import { formatTime, getDurationBetweenTimestamps } from 'utils/datetime';

import styles from 'common/ActivityChip/styles.module.css';

const MIN_ACTION_MENU_WIDTH = 60;

const getElementWidth = (element) => element?.getBoundingClientRect?.().width ?? 0;

const ExtraActivityLayout = ({
	actions = [],
	config,
	timezone,
	isStartInTimeWindow = true,
	isEndInTimeWindow = true,
	onActionMenuInteractionChange,
}) => {
	const chipRef = useRef(null);
	const [chipWidth, setChipWidth] = useState(0);
	const { fromTimestamp, toTimestamp, icon, header } = config;
	const chipStyles = classNames(styles.extra, {
		[styles.openStart]: !isStartInTimeWindow,
		[styles.openEnd]: !isEndInTimeWindow,
	});
	const hasVisibleActions = actions.length > 0 && chipWidth > MIN_ACTION_MENU_WIDTH;

	const updateChipWidth = useCallback((nextWidth = getElementWidth(chipRef.current)) => {
		setChipWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
	}, []);

	useEffect(() => {
		const chipElement = chipRef.current;

		updateChipWidth();

		if (!chipElement || typeof ResizeObserver === 'undefined') return undefined;

		const resizeObserver = new ResizeObserver(([entry]) => {
			updateChipWidth(entry?.contentRect?.width ?? getElementWidth(chipElement));
		});

		resizeObserver.observe(chipElement);

		return () => resizeObserver.disconnect();
	}, [updateChipWidth]);

	return (
		<div ref={chipRef} className={chipStyles}>
			{hasVisibleActions && (
				<div className={styles.actionMenuSlot}>
					<ActionMenu items={actions} onOpenChange={onActionMenuInteractionChange} />
				</div>
			)}
			<span className={styles.extraIcon}>{icon}</span>
			<span className={styles.extraHeader}>{header}</span>
			<div className={styles.extraTimeRow}>
				<span className={styles.extraStartTime}>{formatTime(fromTimestamp, { timezone })}</span>
				<div className={styles.duration}>
					{getDurationBetweenTimestamps(fromTimestamp, toTimestamp)}
				</div>
			</div>
		</div>
	);
};

export default ExtraActivityLayout;
